<template>
  <v-card-text class="card-content pa-0">
    <div class="stats-dashboard-layout">
      <!-- Gesamtzahl als große Karte links -->
      <div class="total-stat-card" @click="openTrendModal">
        <div class="total-stat-content">
          <v-icon size="x-large" color="info">mdi-calendar-month</v-icon>
          <div>
            <div class="total-value">{{ dashboardData.monthly.total }}</div>
            <div class="total-label">Gesamt diesen Monat</div>
          </div>
        </div>

        <!-- Hover-Hinweis -->
        <div class="trend-hint">
          <v-icon size="small" color="info">mdi-chart-line</v-icon>
          <span>Klicken für Trend-Analyse</span>
        </div>
      </div>

      <!-- Einzelne Statistiken rechts als 2x3 Grid -->
      <div class="stats-metrics-container">
        <div class="stats-metrics-grid">
          <div
            v-for="(value, key) in filteredMonthlyStats"
            :key="`monthly-${key}`"
            class="metric-item"
            :style="{
              '--metric-color': getStatColor(key),
              '--metric-percent': calculatePercentage(value, dashboardData.monthly.total) + '%'
            }"
            @click="openDetailModal(key)"
          >
            <div class="metric-content">
              <div class="metric-value">{{ value }}</div>
              <div class="metric-label">{{ getStatLabel(key) }}</div>
            </div>
            <div class="metric-progress"></div>
            <div class="metric-percentage">
              {{ Math.round(calculatePercentage(value, dashboardData.monthly.total)) }}%
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-card-text>

  <!-- Trend Modal -->
  <v-dialog
    v-model="showTrendModal"
    :max-width="isMobile ? '95%' : '700'"
    persistent
    @keydown.esc="closeTrendModal"
  >
    <v-card class="trend-modal-card">
      <v-card-title class="trend-modal-title d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="info" class="mr-2" size="24">mdi-chart-line</v-icon>
          <span>Trend-Analyse - Letzte 3 Monate</span>
        </div>
        <v-btn icon variant="text" @click="closeTrendModal" class="ml-2">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4" v-if="trendData.length > 0">
        <!-- Statistik-Header -->
        <div class="trend-stats-header mb-4">
          <div class="d-flex justify-space-between align-center">
            <div class="stat-item">
              <div class="stat-value text-info">{{ calculateAverage(trendData) }}</div>
              <div class="stat-label">Durchschnitt</div>
            </div>
            <div class="stat-item">
              <div class="stat-value text-success">{{ Math.max(...trendData.map(d => d.total)) }}</div>
              <div class="stat-label">Maximum</div>
            </div>
            <div class="stat-item">
              <div class="stat-value text-warning">{{ Math.min(...trendData.map(d => d.total)) }}</div>
              <div class="stat-label">Minimum</div>
            </div>
            <div class="stat-item">
              <div class="stat-value text-secondary">{{ calculateTrend(trendData) }}</div>
              <div class="stat-label">Trend</div>
            </div>
          </div>
        </div>

        <!-- Größerer Chart -->
        <div class="trend-chart-container">
          <svg width="100%" height="200" viewBox="0 0 520 200">
            <!-- Gitter -->
            <defs>
              <pattern id="grid-monthly-modal" width="60" height="40" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-monthly-modal)" />

            <!-- Geschwungene Kurve -->
            <path :d="modalTrendPath" fill="none" stroke="#2196F3" stroke-width="3" opacity="0.9"/>

            <!-- Glow-Effekt -->
            <path :d="modalTrendPath" fill="none" stroke="#2196F3" stroke-width="6" opacity="0.3"/>

            <!-- Durchschnittslinie -->
            <line
              :x1="40"
              :y1="modalAverageLineY"
              :x2="480"
              :y2="modalAverageLineY"
              stroke="#2196F3"
              stroke-width="2"
              stroke-dasharray="8,4"
              opacity="0.7"
            />

            <!-- Durchschnitts-Label -->
            <text
              :x="485"
              :y="modalAverageLineY + 5"
              fill="#2196F3"
              font-size="12"
              font-weight="600"
            >
              Ø {{ calculateAverage(trendData) }}
            </text>

            <!-- Datenpunkte -->
            <circle
              v-for="(point, index) in modalTrendPoints"
              :key="index"
              :cx="point.x"
              :cy="point.y"
              r="5"
              fill="#2196F3"
              stroke="rgba(255,255,255,0.9)"
              stroke-width="2"
              class="trend-point-modal"
            />

            <!-- Werte über den Punkten -->
            <text
              v-for="(point, index) in modalTrendPoints"
              :key="`value-${index}`"
              :x="point.x"
              :y="point.y - 12"
              fill="rgba(255,255,255,0.9)"
              font-size="11"
              font-weight="500"
              text-anchor="middle"
            >
              {{ trendData[index].total }}
            </text>
          </svg>
        </div>

        <!-- Datenlabels -->
        <div class="trend-labels-modal d-flex justify-space-between mt-3">
          <div v-for="trend in trendData" :key="trend.date" class="trend-label-modal text-center">
            <div class="trend-date-modal">{{ trend.label }}</div>
          </div>
        </div>
      </v-card-text>

      <v-card-text v-else-if="loadingTrend" class="text-center pa-6">
        <v-progress-circular color="info" indeterminate></v-progress-circular>
        <div class="mt-2">Lade Trend-Daten...</div>
      </v-card-text>

      <v-card-text v-else class="text-center pa-6">
        <v-icon size="48" color="grey">mdi-chart-line-variant</v-icon>
        <div class="text-grey mt-2">Keine Trend-Daten verfügbar</div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Detail Modal für einzelne Kategorien -->
  <v-dialog
    v-model="showDetailModal"
    :max-width="isMobile ? '95%' : '600'"
    persistent
    @keydown.esc="closeDetailModal"
  >
    <v-card class="detail-modal-card">
      <v-card-title class="detail-modal-title d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon :color="getStatColor(selectedCategory)" class="mr-2" size="24">
            {{ getCategoryIcon(selectedCategory) }}
          </v-icon>
          <span>{{ getStatLabel(selectedCategory) }} - Detailansicht</span>
        </div>
        <v-btn icon variant="text" @click="closeDetailModal" class="ml-2">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4" v-if="detailData.length > 0 && !loadingDetail">
        <!-- Gesamtzahl Header -->
        <div class="detail-header mb-4">
          <div class="detail-total-card">
            <div class="detail-total-value">{{ calculateDetailTotal(detailData) }}</div>
            <div class="detail-total-label">Gesamt</div>
          </div>
        </div>

        <!-- Liste der Gerätetypen -->
        <div class="detail-list">
          <div
            v-for="(item, index) in detailData"
            :key="index"
            class="detail-item"
            :style="{
              '--item-color': getStatColor(selectedCategory),
              '--item-percent': calculatePercentage(item.anzahl, calculateDetailTotal(detailData)) + '%'
            }"
          >
            <div class="detail-item-content">
              <div class="detail-item-name">{{ item.geraetetyp || 'Unbekannt' }}</div>
              <div class="detail-item-stats">
                <span class="detail-item-value">{{ item.anzahl }}</span>
                <span class="detail-item-percent">
                  {{ Math.round(calculatePercentage(item.anzahl, calculateDetailTotal(detailData))) }}%
                </span>
              </div>
            </div>
            <div class="detail-item-bar"></div>
          </div>
        </div>
      </v-card-text>

      <v-card-text v-else-if="loadingDetail" class="text-center pa-6">
        <v-progress-circular :color="getStatColor(selectedCategory)" indeterminate></v-progress-circular>
        <div class="mt-2">Lade Details...</div>
      </v-card-text>

      <v-card-text v-else class="text-center pa-6">
        <v-icon size="48" color="grey">mdi-information-outline</v-icon>
        <div class="text-grey mt-2">Keine Details verfügbar</div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { inject, computed, ref } from "vue";
import axios from "axios";
import { API_BASE_URL } from "../../config";

// Inject benötigte Daten und Funktionen
const dashboardData = inject("dashboardData");
const getStatLabel = inject("getStatLabel");
const getStatColor = inject("getStatColor");
const calculatePercentage = inject("calculatePercentage");
const isMobile = inject("isMobile", ref(false));

// Trend-Daten State
const trendData = ref([]);
const showTrendModal = ref(false);
const loadingTrend = ref(false);

// Detail-Daten State
const detailData = ref([]);
const showDetailModal = ref(false);
const loadingDetail = ref(false);
const selectedCategory = ref('');
const currentMonthYear = ref('');

// Gefilterte Stats für die Visualisierung (ohne 'total')
const filteredMonthlyStats = computed(() => {
  const result = { ...dashboardData.value.monthly };
  delete result.total;
  return result;
});

// Berechne SVG-Pfad für geschwungene Kurve
const trendPath = computed(() => {
  if (trendData.value.length < 2) return '';

  const points = trendPoints.value;
  if (points.length < 2) return '';

  let path = `M ${points[0].x} ${points[0].y}`;

  // Erstelle geschwungene Kurve mit Bézier-Kurven
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    // Kontrollpunkte für sanfte Kurve
    const cpX1 = prev.x + (curr.x - prev.x) * 0.3;
    const cpY1 = prev.y;
    const cpX2 = curr.x - (curr.x - prev.x) * 0.3;
    const cpY2 = curr.y;

    path += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${curr.x} ${curr.y}`;
  }

  return path;
});

// Berechne Koordinaten für Datenpunkte
const trendPoints = computed(() => {
  if (trendData.value.length === 0) return [];

  const maxValue = Math.max(...trendData.value.map(d => d.total));
  const minValue = Math.min(...trendData.value.map(d => d.total));
  const range = maxValue - minValue || 1;

  const width = 280;
  const height = 100;
  const padding = 20;

  return trendData.value.map((trend, index) => ({
    x: padding + (index * (width - padding * 2)) / (trendData.value.length - 1),
    y: height - padding - ((trend.total - minValue) / range) * (height - padding * 2)
  }));
});

// Berechne Y-Position der Durchschnittslinie
const averageLineY = computed(() => {
  if (trendData.value.length === 0) return 50;

  const maxValue = Math.max(...trendData.value.map(d => d.total));
  const minValue = Math.min(...trendData.value.map(d => d.total));
  const range = maxValue - minValue || 1;
  const avgValue = calculateAverage(trendData.value);

  const height = 100;
  const padding = 20;

  return height - padding - ((avgValue - minValue) / range) * (height - padding * 2);
});

// Modal Chart - größere Dimensionen
const modalTrendPoints = computed(() => {
  if (trendData.value.length === 0) return [];

  const maxValue = Math.max(...trendData.value.map(d => d.total));
  const minValue = Math.min(...trendData.value.map(d => d.total));
  const range = maxValue - minValue || 1;

  const width = 520;
  const height = 200;
  const padding = 40;

  return trendData.value.map((trend, index) => ({
    x: padding + (index * (width - padding * 2)) / (trendData.value.length - 1),
    y: height - padding - ((trend.total - minValue) / range) * (height - padding * 2)
  }));
});

const modalTrendPath = computed(() => {
  if (trendData.value.length < 2) return '';

  const points = modalTrendPoints.value;
  if (points.length < 2) return '';

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];

    const cpX1 = prev.x + (curr.x - prev.x) * 0.3;
    const cpY1 = prev.y;
    const cpX2 = curr.x - (curr.x - prev.x) * 0.3;
    const cpY2 = curr.y;

    path += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${curr.x} ${curr.y}`;
  }

  return path;
});

const modalAverageLineY = computed(() => {
  if (trendData.value.length === 0) return 100;

  const maxValue = Math.max(...trendData.value.map(d => d.total));
  const minValue = Math.min(...trendData.value.map(d => d.total));
  const range = maxValue - minValue || 1;
  const avgValue = calculateAverage(trendData.value);

  const height = 200;
  const padding = 40;

  return height - padding - ((avgValue - minValue) / range) * (height - padding * 2);
});

// Modal öffnen und Daten laden
const openTrendModal = async () => {
  showTrendModal.value = true;

  if (trendData.value.length > 0) return;

  loadingTrend.value = true;

  try {
    const response = await axios.post(`${API_BASE_URL}dashboard_trends.php`, {
      action: 'get_trends',
      type: 'monthly'
    }, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      trendData.value = response.data.trends;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Trend-Daten:', error);
  } finally {
    loadingTrend.value = false;
  }
};

// Modal schließen
const closeTrendModal = () => {
  showTrendModal.value = false;
};

// Berechne Durchschnitt der Trend-Daten
const calculateAverage = (data) => {
  if (!data || data.length === 0) return 0;
  const sum = data.reduce((acc, item) => acc + item.total, 0);
  return Math.round(sum / data.length);
};

// Berechne Trend (steigend/fallend)
const calculateTrend = (data) => {
  if (!data || data.length < 2) return '📊';

  const firstHalf = data.slice(0, Math.floor(data.length / 2));
  const secondHalf = data.slice(Math.floor(data.length / 2));

  const firstAvg = firstHalf.reduce((acc, item) => acc + item.total, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((acc, item) => acc + item.total, 0) / secondHalf.length;

  const diff = secondAvg - firstAvg;
  const percentChange = Math.round((diff / firstAvg) * 100);

  if (percentChange > 5) return '📈';
  if (percentChange < -5) return '📉';
  return '➡️';
};

// Detail-Modal Funktionen
const openDetailModal = async (category) => {
  selectedCategory.value = category;
  showDetailModal.value = true;

  // Aktuellen Monat und Jahr ermitteln
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  currentMonthYear.value = `${year}-${month}-01`;

  loadingDetail.value = true;
  detailData.value = [];

  try {
    const response = await axios.post(`${API_BASE_URL}dashboard_trends.php`, {
      action: 'get_detail',
      type: 'monthly',
      date: currentMonthYear.value,
      category: category
    }, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      detailData.value = response.data.details;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Detail-Daten:', error);
  } finally {
    loadingDetail.value = false;
  }
};

const closeDetailModal = () => {
  showDetailModal.value = false;
  detailData.value = [];
  selectedCategory.value = '';
};

const calculateDetailTotal = (data) => {
  if (!data || data.length === 0) return 0;
  return data.reduce((sum, item) => sum + parseInt(item.anzahl || 0), 0);
};

const getCategoryIcon = (category) => {
  const icons = {
    'instandsetzungen': 'mdi-wrench',
    'retouren': 'mdi-package-variant-closed-remove',
    'reparatur': 'mdi-tools',
    'display': 'mdi-cellphone-screenshot',
    'backcover': 'mdi-cellphone-back',
    'akku': 'mdi-battery'
  };
  return icons[category] || 'mdi-information';
};
</script>

<style scoped>
.card-content {
  padding: 0;
}

/* Neues zweispaltiges Layout */
.stats-dashboard-layout {
  display: grid;
  grid-template-columns: 1fr 2fr; /* 1/3 für Gesamt, 2/3 für die Details */
  gap: 12px;
  padding: 16px;
}

/* Gesamtstatistik-Karte (links) */
.total-stat-card {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.05));
  border-radius: 12px;
  padding: 24px;
  border: 1px solid rgba(33, 150, 243, 0.2);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%; /* Volle Höhe */
  position: relative;
  cursor: pointer;
}

.total-stat-card:hover {
  box-shadow: 0 4px 20px rgba(33, 150, 243, 0.2);
  transform: translateY(-2px);
}

.total-stat-card:hover .trend-hint {
  opacity: 1;
  transform: translateY(0);
}

/* Trend-Hinweis */
.trend-hint {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  color: rgba(33, 150, 243, 0.8);
  opacity: 0;
  transition: all 0.3s ease;
  pointer-events: none;
}

/* Modal Styling */
.trend-modal-card {
  background: rgba(20, 20, 30, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(33, 150, 243, 0.3);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.trend-modal-title {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.05));
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  font-weight: 600;
  padding: 16px 20px;
}

.trend-stats-header {
  background: rgba(30, 30, 40, 0.5);
  border-radius: 8px;
  padding: 16px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.stat-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.trend-chart-container {
  background: rgba(10, 10, 20, 0.3);
  border-radius: 8px;
  padding: 16px;
  overflow: hidden;
}

.trend-point-modal {
  transition: r 0.2s ease;
  cursor: pointer;
}

.trend-point-modal:hover {
  r: 7;
}

.trend-labels-modal {
  margin-top: 12px;
}

.trend-label-modal {
  flex: 1;
}

.trend-date-modal {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

/* Trend-Tooltip */
.trend-tooltip {
  position: fixed;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(20, 20, 30, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(33, 150, 243, 0.4);
  border-radius: 12px;
  padding: 16px;
  width: 320px;
  z-index: 9999;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(33, 150, 243, 0.2);
  animation: fadeInUp 0.3s ease;
}

.trend-tooltip::before {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid rgba(20, 20, 30, 0.98);
  display: none; /* Pfeil verstecken bei fixed position */
}

.trend-header {
  text-align: center;
  margin-bottom: 12px;
}

.trend-header h4 {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
}

.trend-average {
  color: rgba(33, 150, 243, 0.9);
  font-size: 0.8rem;
  font-weight: 600;
  margin-top: 4px;
}

.trend-chart {
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(10, 10, 20, 0.3);
}

.trend-point {
  transition: r 0.2s ease;
  cursor: pointer;
}

.trend-point:hover {
  r: 5;
}

.trend-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}

.trend-label {
  text-align: center;
  flex: 1;
}

.trend-date {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 2px;
}

.trend-value {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.total-stat-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.total-value {
  font-size: 3.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1;
  margin-top: 8px;
}

.total-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

/* Container für das rechte Grid */
.stats-metrics-container {
  display: flex;
  flex-direction: column;
}

/* Metriken-Grid (rechts) - 2x3 */
.stats-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr); /* 3 Spalten */
  grid-template-rows: repeat(2, 1fr);    /* 2 Zeilen */
  gap: 10px;
  height: 100%;
}

.metric-item {
  position: relative;
  background: rgba(30, 30, 40, 0.5);
  border-radius: 10px;
  padding: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
}

.metric-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  border-color: var(--metric-color);
}

.metric-content {
  position: relative;
  z-index: 2;
}

.metric-value {
  font-size: 1.4rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 2px;
}

.metric-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}

.metric-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  width: var(--metric-percent);
  height: 4px;
  background-color: var(--metric-color);
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0 4px 0 0;
}

.metric-percentage {
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 0.7rem;
  color: var(--metric-color);
  font-weight: 500;
  opacity: 0.9;
}

/* Animations */
.metric-item {
  animation: fadeIn 0.5s ease forwards;
  opacity: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.metric-item:nth-child(1) { animation-delay: 0.1s; }
.metric-item:nth-child(2) { animation-delay: 0.15s; }
.metric-item:nth-child(3) { animation-delay: 0.2s; }
.metric-item:nth-child(4) { animation-delay: 0.25s; }
.metric-item:nth-child(5) { animation-delay: 0.3s; }
.metric-item:nth-child(6) { animation-delay: 0.35s; }

/* Responsive Anpassungen */
@media (max-width: 960px) {
  .stats-dashboard-layout {
    grid-template-columns: 1fr; /* Einspaltiges Layout auf kleineren Bildschirmen */
    grid-template-rows: auto auto;
  }

  .total-stat-card {
    padding: 16px;
  }

  .total-stat-content {
    flex-direction: row;
    text-align: left;
  }

  .total-value {
    font-size: 2.5rem;
  }

  .stats-metrics-grid {
    grid-template-columns: repeat(3, 1fr); /* 3 Spalten beibehalten */
    margin-top: 12px;
  }
}

@media (max-width: 768px) {
  .stats-metrics-grid {
    grid-template-columns: repeat(2, 1fr); /* 2 Spalten auf Tablets */
    grid-template-rows: auto;
  }
}

@media (max-width: 480px) {
  .stats-metrics-grid {
    grid-template-columns: 1fr; /* 1 Spalte auf kleinen Mobilgeräten */
  }

  .metric-item {
    padding: 10px;
  }

  .metric-value {
    font-size: 1.2rem;
  }
}

/* Detail Modal Styles */
.detail-modal-card {
  background: rgba(20, 20, 30, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(33, 150, 243, 0.3);
  animation: modalSlideIn 0.3s ease;
}

.detail-modal-title {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.05));
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  font-weight: 600;
  padding: 16px 20px;
}

.detail-header {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.detail-total-card {
  background: rgba(30, 30, 40, 0.5);
  border-radius: 12px;
  padding: 20px 40px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-total-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1;
}

.detail-total-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

.detail-item {
  position: relative;
  background: rgba(30, 30, 40, 0.4);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.detail-item:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border-color: var(--item-color);
}

.detail-item-content {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-item-name {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.detail-item-stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-item-value {
  font-size: 1.3rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.detail-item-percent {
  font-size: 0.85rem;
  color: var(--item-color);
  font-weight: 500;
  min-width: 45px;
  text-align: right;
}

.detail-item-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: var(--item-percent);
  height: 4px;
  background: linear-gradient(90deg, var(--item-color), transparent);
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0.6;
}

/* Scrollbar für Detail-Liste */
.detail-list::-webkit-scrollbar {
  width: 6px;
}

.detail-list::-webkit-scrollbar-track {
  background: transparent;
}

.detail-list::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.detail-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}
</style>
